import {
  CartItem,
  CartItemDesign,
  CartItemDesignInstance,
  CartItemDesignInstanceLogo,
  CartItemLogo,
} from "../presentation/screens/order-create/models/CartItem";
import { BaseLogo } from "../core/models/entities/BaseLogo";
import { Design, DesignLogo } from "../core/models/entities/Design";

// Cart persist slim (flag REACT_APP_CART_PERSIST_SLIM_ENABLED, 20/10).
// CartContext hands its items through slimCartItemsForPersist - with the
// flag on only - right before the v1 writer (slimItemsForStorage) and the
// v2 writer (serializeCartForStorage); the fallback's IndexedDB shadow gets
// the same string. The in-memory items are never touched.
//
// What goes: the `organizations` list on the design and on every logo copy
// (design.designLogos[].logo, excludedDeletedDesignLogos[].logo,
// instances[].logos[].baseLogo, a logo line's baseLogo). The API's
// DesignDto.Organizations / LogoDto.Organizations are not on the TS
// entities - they ride onto the cart line through the mapper spread
// (`...dto`) - and no cart, checkout or order reader touches them (grep
// over src: the only `.organizations` readers are admin / billing screens).
// Prod 20/10 (act-as Sport 24 Business Silkeborg, 3 Hummel designs a 10
// rows): 80 orgs on the design = 6,3 M chars, 86 on each logo = 6,5 M chars
// per logo copy; the bucket was 45,2 M chars against Chrome's 5-10 MB quota
// (QuotaExceeded -> "the cart is empty after a refresh", the 30/9 reports).
// Everything else is kept - customer / vendor, product, prices, approval,
// style, designLogos[].view, the instance copies - exactly as today.
// Pure and structural: one shallow map over the lines, no JSON round trip.

type WithOrganizations = { organizations?: unknown };

function dropOrganizations<T>(value: T): T {
  if (value == null || typeof value !== "object") return value;
  if (!("organizations" in (value as object))) return value;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { organizations, ...rest } = value as T & WithOrganizations;
  return rest as T;
}

function slimDesignLogo(dl: DesignLogo): DesignLogo {
  if (!dl || typeof dl !== "object" || !dl.logo) return dl;
  return { ...dl, logo: dropOrganizations<BaseLogo>(dl.logo) };
}

function slimDesign(design: Design): Design {
  if (!design || typeof design !== "object") return design;
  const slim = dropOrganizations(design);
  if (!Array.isArray(design.designLogos)) return slim;
  return { ...slim, designLogos: design.designLogos.map(slimDesignLogo) };
}

function slimInstanceLogo(logo: CartItemDesignInstanceLogo): CartItemDesignInstanceLogo {
  if (!logo || typeof logo !== "object" || !logo.baseLogo) return logo;
  return { ...logo, baseLogo: dropOrganizations<BaseLogo>(logo.baseLogo) };
}

function slimInstance(inst: CartItemDesignInstance): CartItemDesignInstance {
  if (!inst || typeof inst !== "object" || !Array.isArray(inst.logos)) return inst;
  return { ...inst, logos: inst.logos.map(slimInstanceLogo) };
}

function slimDesignItem(item: CartItemDesign): CartItemDesign {
  const slim: CartItemDesign = { ...item };
  if ("design" in item) slim.design = slimDesign(item.design);
  if (Array.isArray(item.instances)) slim.instances = item.instances.map(slimInstance);
  if (Array.isArray(item.excludedDeletedDesignLogos)) {
    slim.excludedDeletedDesignLogos = item.excludedDeletedDesignLogos.map(slimDesignLogo);
  }
  return slim;
}

function slimLogoItem(item: CartItemLogo): CartItemLogo {
  if (!item.baseLogo || typeof item.baseLogo !== "object") return item;
  return { ...item, baseLogo: dropOrganizations<BaseLogo>(item.baseLogo) };
}

export function slimCartItemsForPersist(items: readonly CartItem[]): CartItem[] {
  return (items ?? []).map((item) => {
    if (!item || typeof item !== "object") return item;
    if (item.type === "Design") return slimDesignItem(item as CartItemDesign);
    if (item.type === "Logo") return slimLogoItem(item as CartItemLogo);
    return item;
  });
}
